import React, { useEffect, useState } from "react";
import { api, formatError } from "../lib/api";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { HeartHandshake, Gift, CreditCard, Smartphone, CalendarHeart } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { useRazorpay, payWithRazorpay } from "../lib/razorpay";

const PRESETS = [21, 51, 108, 501, 1008];

export default function Donate() {
  const [sevas, setSevas] = useState([]);
  const [sevaKey, setSevaKey] = useState("general");
  const [amount, setAmount] = useState(108);
  const [custom, setCustom] = useState("");
  const [form, setForm] = useState({ donor_name: "", donor_email: "", gotra: "" });
  const [paying, setPaying] = useState(false);
  const [isGift, setIsGift] = useState(false);
  const [gift, setGift] = useState({ gift_recipient_name: "", gift_recipient_email: "", gift_message: "" });
  const [provider, setProvider] = useState("stripe");
  const rzp = useRazorpay();
  const [params] = useSearchParams();
  const [festival, setFestival] = useState(null);

  useEffect(() => { api.get("/sevas").then((r) => setSevas(r.data.items)); }, []);
  useEffect(() => {
    const slug = params.get("festival");
    if (slug) api.get(`/festivals/${slug}`).then((r) => setFestival(r.data)).catch(() => {});
  }, [params]);

  const finalAmount = custom ? parseFloat(custom) || 0 : amount;

  const donate = async () => {
    if (provider === "razorpay" && !rzp.enabled) {
      toast.info("UPI via Razorpay is coming soon. Please use card for now.");
      return;
    }
    if (finalAmount <= 0) return toast.error("Please choose or enter an amount");
    if (!form.donor_email) return toast.error("Email is required for your receipt");
    if (isGift && !gift.gift_recipient_email) return toast.error("Recipient email is required for gift seva");
    setPaying(true);
    const payload = {
      amount_usd: finalAmount,
      seva_key: sevaKey,
      donor_name: form.donor_name,
      donor_email: form.donor_email,
      gotra: festival ? `${form.gotra ? `${form.gotra} · ` : ""}${festival.name} seva` : form.gotra,
      gift_recipient_name: isGift ? gift.gift_recipient_name : null,
      gift_recipient_email: isGift ? gift.gift_recipient_email : null,
      gift_message: isGift ? gift.gift_message : null,
      origin_url: window.location.origin,
    };
    if (provider === "razorpay") return payWithRazorpay("/razorpay/donate/order", payload, () => setPaying(false));
    try {
      const { data } = await api.post("/payments/donate/checkout", payload);
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(formatError(e)); setPaying(false); }
  };

  return (
    <div data-testid="donate-page" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
      <div className="max-w-3xl">
        <div className="overline">॥ सेवा और दान ॥</div>
        <h1 className="font-serif text-5xl lg:text-6xl text-[#4A0E17] mt-2 leading-tight">Offer a Seva</h1>
        <p className="text-[#63534E] mt-5 text-lg leading-relaxed">
          Every seva reaches the altar carrying your name and gotra. Choose how you'd like to serve — then any amount, however small, becomes a prayer.
        </p>
      </div>

      <div className="grid lg:grid-cols-5 gap-10 mt-14">
        {festival && (
          <div data-testid="festival-seva-banner" className="lg:col-span-5 flex items-center gap-4 bg-[#F3E5AB]/50 border border-[#D4AF37]/50 rounded-2xl p-5">
            <CalendarHeart className="w-6 h-6 text-[#E65100] shrink-0" />
            <div><div className="font-serif text-xl text-[#4A0E17]">{festival.name} seva</div><div className="text-sm text-[#63534E]">{festival.date} · in {festival.days_away} days · your offering will be dedicated to this festival</div></div>
          </div>
        )}
        <div className="lg:col-span-3 space-y-10">
          <div>
            <div className="overline">1. Choose a Seva</div>
            <div className="grid sm:grid-cols-2 gap-4 mt-5">
              {sevas.map((s) => (
                <button
                  key={s.key}
                  data-testid={`seva-option-${s.key}`}
                  onClick={() => setSevaKey(s.key)}
                  className={`text-left bg-[#FFFDF9] rounded-2xl p-5 transition-sacred border ${sevaKey === s.key ? "border-[#E65100] ring-2 ring-[#E65100]/30" : "border-[#D4AF37]/40 hover:border-[#D4AF37]"}`}
                >
                  <div className="font-serif text-xl text-[#4A0E17]">{s.title}</div>
                  <p className="text-sm text-[#63534E] mt-2 leading-relaxed">{s.description}</p>
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="overline">2. Choose an amount (USD)</div>
            <div className="flex flex-wrap gap-3 mt-5">
              {PRESETS.map((a) => (
                <button
                  key={a}
                  data-testid={`donation-amount-${a}`}
                  onClick={() => { setAmount(a); setCustom(""); }}
                  className={`px-6 h-14 rounded-full font-mono text-lg font-semibold transition-sacred border-2 ${amount === a && !custom ? "bg-[#E65100] text-white border-[#E65100]" : "bg-[#FFFDF9] text-[#4A0E17] border-[#D4AF37]/40 hover:border-[#D4AF37]"}`}
                >
                  ${a}
                </button>
              ))}
              <Input
                data-testid="donation-custom-amount"
                type="number"
                min="1"
                placeholder="Custom $"
                value={custom}
                onChange={(e) => setCustom(e.target.value)}
                className="w-40 h-14 rounded-full bg-[#FFFDF9] border-[#D4AF37]/40 text-center font-mono text-lg"
              />
            </div>
          </div>

          <div>
            <div className="overline">3. Your details for the sankalpa</div>
            <div className="grid sm:grid-cols-2 gap-4 mt-5">
              <Input data-testid="donor-name" placeholder="Full name" value={form.donor_name} onChange={(e) => setForm({ ...form, donor_name: e.target.value })} className="bg-[#FFFDF9] border-[#D4AF37]/40 h-12" />
              <Input data-testid="donor-email" type="email" placeholder="Email for receipt" value={form.donor_email} onChange={(e) => setForm({ ...form, donor_email: e.target.value })} className="bg-[#FFFDF9] border-[#D4AF37]/40 h-12" />
              <Input data-testid="donor-gotra" placeholder="Gotra (optional)" value={form.gotra} onChange={(e) => setForm({ ...form, gotra: e.target.value })} className="bg-[#FFFDF9] border-[#D4AF37]/40 h-12 sm:col-span-2" />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between">
              <div className="overline">4. Gift this seva (optional)</div>
              <label className="flex items-center gap-2 text-sm text-[#4A0E17]">
                <input data-testid="toggle-gift" type="checkbox" checked={isGift} onChange={(e) => setIsGift(e.target.checked)} />
                I'm gifting this to someone
              </label>
            </div>
            {isGift && (
              <div data-testid="gift-fields" className="mt-5 bg-[#F3E5AB]/40 border border-[#D4AF37]/40 rounded-2xl p-5 space-y-3">
                <div className="flex items-center gap-2 text-[#4A0E17]"><Gift className="w-4 h-4" /> <span className="font-semibold text-sm">Gift details</span></div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <Input data-testid="gift-name" placeholder="Recipient's name" value={gift.gift_recipient_name} onChange={(e) => setGift({ ...gift, gift_recipient_name: e.target.value })} className="bg-white border-[#D4AF37]/40 h-11" />
                  <Input data-testid="gift-email" type="email" placeholder="Recipient's email" value={gift.gift_recipient_email} onChange={(e) => setGift({ ...gift, gift_recipient_email: e.target.value })} className="bg-white border-[#D4AF37]/40 h-11" />
                </div>
                <Textarea data-testid="gift-message" placeholder="Add a personal blessing (optional)" rows={2} value={gift.gift_message} onChange={(e) => setGift({ ...gift, gift_message: e.target.value })} className="bg-white border-[#D4AF37]/40" />
                <p className="text-xs text-[#63534E]">A beautifully composed blessing email will be sent to the recipient on successful payment.</p>
              </div>
            )}
          </div>

          <div>
            <div className="overline">5. Payment method</div>
            <div className="grid sm:grid-cols-2 gap-3 mt-5">
              <button data-testid="provider-stripe" onClick={() => setProvider("stripe")}
                className={`text-left p-4 rounded-2xl border transition-sacred flex items-center gap-3 ${provider === "stripe" ? "bg-[#4A0E17] text-[#F3E5AB] border-[#4A0E17]" : "bg-[#FFFDF9] text-[#4A0E17] border-[#D4AF37]/40"}`}>
                <CreditCard className="w-5 h-5" />
                <div>
                  <div className="font-semibold text-sm">Card · International</div>
                  <div className="text-xs opacity-80">Secured by Stripe</div>
                </div>
              </button>
              <button data-testid="provider-razorpay" onClick={() => setProvider("razorpay")}
                className={`text-left p-4 rounded-2xl border transition-sacred flex items-center gap-3 relative ${provider === "razorpay" ? "bg-[#4A0E17] text-[#F3E5AB] border-[#4A0E17]" : "bg-[#FFFDF9] text-[#4A0E17] border-[#D4AF37]/40"}`}>
                <Smartphone className="w-5 h-5" />
                <div>
                  <div className="font-semibold text-sm">UPI · India (Razorpay)</div>
                  <div className="text-xs opacity-80">{rzp.enabled ? `UPI, cards, netbanking · ₹${Math.round(finalAmount * rzp.inr_per_usd)}` : "Coming soon"}</div>
                </div>
                {!rzp.enabled && <span className="absolute top-2 right-3 overline text-[#E65100] bg-[#F3E5AB]/70 px-2 py-0.5 rounded-full text-[0.6rem]">Soon</span>}
              </button>
            </div>
          </div>
        </div>

        <aside className="lg:col-span-2 bg-[#4A0E17] text-[#FAF6F0] rounded-3xl p-8 h-fit lg:sticky lg:top-24 relative overflow-hidden">
          <div className="absolute -top-10 -right-5 font-devanagari text-[220px] leading-none text-[#D4AF37] opacity-10 pointer-events-none">ॐ</div>
          <HeartHandshake className="w-10 h-10 text-[#D4AF37]" />
          <div className="overline text-[#D4AF37] mt-5">Your offering</div>
          <div className="font-mono text-5xl mt-2 font-semibold">${finalAmount.toFixed(2)}</div>
          <div className="mt-1 text-sm text-[#F3E5AB]/80 capitalize">{sevas.find((s) => s.key === sevaKey)?.title || sevaKey}</div>
          <Button data-testid="donate-pay-button" disabled={paying} onClick={donate} className="mt-7 w-full bg-[#E65100] hover:bg-[#BF360C] rounded-full h-13 text-base py-4">
            {paying ? "Processing…" : "Proceed to secure payment"}
          </Button>
          <p className="text-xs text-[#F3E5AB]/70 mt-4 leading-relaxed">
            Secured by {provider === "razorpay" ? "Razorpay" : "Stripe"}. All donations contribute to Pancham Dham operations, daily sevas and construction. Tax receipt emailed on successful payment.
          </p>
        </aside>
      </div>
    </div>
  );
}
